// src/.../tasklist/tasks/TemplateTasksMain.tsx
import React, { useCallback, useEffect, useState } from "react";
import { getXSRFToken } from "../../api/v2/ApiUtils";
import { createDefaultTask, createDefaultSubtask, updateDefaultSubtask, getDefaultModule, deleteDefaultSubtask } from "../../api/v2/TasklistsApi";
import { DefaultPhaseDto } from "../../dtos/DefaultPhaseDto";
import { DefaultStepDto } from "../../dtos/DefaultStepDto";
import { GrantDto } from "../../dtos/GrantDto";
import { PhaseDto } from "../../dtos/PhaseDto";
import { PhaseWorkflowType } from "../../dtos/PhaseWorkflowType";
import { StepDto } from "../../dtos/StepDto";
import useAsync from "../../hooks/useAsync";
import useFillViewportHeight from "../../hooks/useFillViewportHeight";
import { useAppContext } from "../shared/AppContext";
import { TaskListProvider, useTaskListActions, useTaskListSelectionState } from "../shared/tasklist/context/TaskListContext";
import Tasks from "../shared/tasklist/tasks/Tasks";
import { mapDefaultPhaseToPhaseDto, mapPhaseDtoToDefaultPhase, mapStepDtoToDefaultStep } from "../shared/tasklist/tasks/adapters/templateMappers";
import { getEffectiveStepType, stepToPhaseWorkflowType } from "../shared/Utils";
import { StepWorkflowType } from "../../dtos/StepWorkflowType";
import TemplateConfigEditor from "./TemplateConfigEditor";

type Props = {
    defaultModuleId: number;
    grantData: GrantDto; // fake grant
    deepLinkHash?: string | null;
    onTemplateChanged?: () => void;
};

function TemplateTasksInner({ defaultModuleId, grantData, deepLinkHash, onTemplateChanged }: Props) {
    const app = useAppContext();
    const taskListActions = useTaskListActions();
    const { selectedStep, selectedPhase } = useTaskListSelectionState();

    const xsrf = useAsync<string | null>(() => getXSRFToken(), []);

    const [phasesInfo, setPhasesInfo] = useState<{ getData: () => Promise<PhaseDto | null> }[]>([]);
    const [allPhaseTypes, setAllPhaseTypes] = useState<DefaultPhaseDto[]>([]);
    const [showConfigEditor, setShowConfigEditor] = useState(false);
    const [editingTask, setEditingTask] = useState<DefaultPhaseDto | null>(null);
    const [editingSubtask, setEditingSubtask] = useState<DefaultStepDto | null>(null);

    const loadModule = useCallback(async () => {
        // Fetch ALL phase types (including non-tasklist) so they're available as
        // targets in the completion trigger dropdowns. The task list itself is
        // filtered to only tasklist phases below.
        const initialResults = await getDefaultModule(defaultModuleId, undefined, false);
        if (initialResults?.error) {
            app.errorToast(initialResults.error.message);
            return;
        }

        const phaseTypes = initialResults?.data?.phaseTypes ?? [];
        const nonDeletedPhaseTypes = phaseTypes.filter(pt => pt && !pt.deleted);
        setAllPhaseTypes(nonDeletedPhaseTypes);

        // Filter to only tasklist phases for the task list display (Mixed modules
        // like DC also contain non-tasklist phases that shouldn't appear here).
        const tasklistPhaseTypes = nonDeletedPhaseTypes.filter(pt => pt.settings?.workflowType != null);

        // Capture the phase data in the closure - no need to re-fetch the entire module
        // When refresh is needed, loadModule() is called which recreates these with fresh data
        const items = tasklistPhaseTypes.map((phaseType) => ({
            getData: async (): Promise<PhaseDto | null> => {
                return phaseType ? mapDefaultPhaseToPhaseDto(phaseType) : null;
            },
        }));

        setPhasesInfo(items);
    }, [defaultModuleId, app]);

    useEffect(() => {
        loadModule();
    }, [loadModule]);

    // Register the refresh handler so child components can trigger a reload
    useEffect(() => {
        taskListActions.registerForRefreshSteps(async () => {
            await loadModule();
        });
    }, [taskListActions, loadModule]);

    // Handle selection changes - use a single effect to avoid race conditions
    useEffect(() => {
        // If a step (subtask) is selected, show the subtask editor
        if (selectedStep?.defaultStep) {
            setEditingSubtask(selectedStep.defaultStep);
            setEditingTask(null);
            setShowConfigEditor(true);
        }
        // Else if a phase (task) is selected, show the task editor
        else if (selectedPhase?.defaultPhase) {
            setEditingTask(selectedPhase.defaultPhase);
            setEditingSubtask(null);
            setShowConfigEditor(true);
        }
        // Nothing selected - clear editor
        else {
            setEditingTask(null);
            setEditingSubtask(null);
            setShowConfigEditor(false);
        }
    }, [selectedPhase, selectedStep]);

    const handleSave = async (data: DefaultPhaseDto | DefaultStepDto) => {
        if (editingTask) {
            // Save task
            const result = await createDefaultTask(defaultModuleId, data as DefaultPhaseDto);
            if (result?.error) {
                app.errorToast(result.error.message);
                return;
            }
            app.infoToast("Task saved successfully");

            // Update editing state with fresh data from API to keep all tabs in sync
            if (result?.data) {
                const savedTask = result.data as DefaultPhaseDto;
                setEditingTask(savedTask);
                // Update selected phase with fresh data
                taskListActions.setSelectedPhase({
                    ...selectedPhase,
                    defaultPhase: savedTask
                } as PhaseDto);
            }
        } else if (editingSubtask) {
            // Save subtask
            const subtaskData = data as DefaultStepDto;
            const isNewSubtask = !subtaskData.id || subtaskData.id === 0;
            const result = isNewSubtask
                ? await createDefaultSubtask(defaultModuleId, subtaskData)
                : await updateDefaultSubtask(defaultModuleId, subtaskData);
            if (result?.error) {
                app.errorToast(result.error.message);
                return;
            }
            app.infoToast("Subtask saved successfully");

            // Always update the editing state with the saved subtask data
            // This ensures all tabs have fresh data (prevents permissions overwriting transitions, etc.)
            if (result?.data) {
                const savedSubtask = result.data as DefaultStepDto;
                setEditingSubtask(savedSubtask);
                // Update the selected step with fresh data
                taskListActions.setSelectedStep({
                    id: String(savedSubtask.id),
                    name: savedSubtask.name,
                    order: savedSubtask.number,
                    description: savedSubtask.description,
                    defaultStep: savedSubtask,
                    phaseSubmissionId: "templatesubmission"
                } as StepDto);
            }
        }

        // Refresh the list
        await loadModule();
        onTemplateChanged?.();
    };

    const handleCloseEditor = () => {
        setShowConfigEditor(false);
        setEditingTask(null);
        setEditingSubtask(null);
        taskListActions.setSelectedStep(null);
        taskListActions.setSelectedPhase(null);
    };

    const handleDeleteSubtask = async () => {
        if (!editingSubtask?.id || !selectedPhase?.defaultPhase?.moduleTypeId) {
            app.errorToast("Unable to delete subtask - missing information");
            return;
        }

        const moduleId = selectedPhase.defaultPhase.moduleTypeId;
        const phaseTypeId = editingSubtask.phaseTypeId;
        const subtaskId = editingSubtask.id;

        const result = await deleteDefaultSubtask(moduleId, phaseTypeId, subtaskId);
        if (result?.error) {
            app.errorToast(result.error.message);
            return;
        }

        app.infoToast("Subtask deleted successfully");
        handleCloseEditor();
        await loadModule();
        onTemplateChanged?.();
    };

    // Parent task's declared type (Phase/Step/None/PhaseHybrid/StepHybrid). Used by the
    // Settings tab to know whether to expose the per-subtask workflow picker.
    const parentWorkflowType = selectedPhase?.defaultPhase?.settings?.workflowType
        ?? PhaseWorkflowType.Phase;

    // Effective type used to drive the existing tabs. When a subtask under a hybrid
    // task is being edited, this resolves to the subtask's own type so all the
    // existing PhaseWorkflowType-based UI keeps working unchanged.
    let workflowType: PhaseWorkflowType;
    if (editingSubtask && selectedPhase?.defaultPhase) {
        workflowType = stepToPhaseWorkflowType(getEffectiveStepType(selectedPhase.defaultPhase, editingSubtask));
    } else {
        workflowType = editingTask?.settings?.workflowType
            ?? parentWorkflowType;
    }

    // The subtask's own workflow type, kept in the step enum rather than mapped into the phase one,
    // for the tabs whose rule is about the subtask itself. When a task is being edited this is the
    // type its NEW subtasks would get.
    const subtaskWorkflowType: StepWorkflowType | null = editingSubtask && selectedPhase?.defaultPhase
        ? getEffectiveStepType(selectedPhase.defaultPhase, editingSubtask)
        : editingTask
            ? getEffectiveStepType(editingTask, null)
            : null;

    // Core subtask templates (seeded by the system) cannot be deleted regardless of
    // the workflow's Delete permission configuration.
    const subtaskIsDeletable = !!editingSubtask && editingSubtask.settings?.isCore !== true;

    const { ref: shellRef, maxHeight: shellHeight } = useFillViewportHeight<HTMLDivElement>({
        gutter: 16,
        minHeight: 260,
    });

    // Always hide the built-in detail view in templates mode - we use our own tab editor
    return (
        // Fills the window rather than standing at a fixed 500px (ticket 1046). The task list
        // inside sizes itself the same way, so a fixed height here clipped it and left the page
        // with two nested scrollbars - the clipped column's and the list's own.
        <div className="template-tasks-shell" ref={shellRef} style={{ display: "flex", height: shellHeight ?? 500 }}>
            {/* Left side: Task/Subtask list */}
            <div style={{
                width: "300px",
                minWidth: "300px",
                flexShrink: 0,
                // No overflow here: the task list scrolls its own rows, and a scrollbar on this
                // column as well would be a second one for the same content. No border or gap
                // either - the tabs beside it are boxed, so their edge is the separator.
            }}>
                <Tasks
                    grantData={grantData}
                    phasesInfo={phasesInfo}
                    deepLinkHash={deepLinkHash ?? null}
                    featureOverrides={{ hideDetailView: true }}
                />
            </div>

            {/* Right side: Config Editor with tabs (includes Summary) */}
            {showConfigEditor ? (
                <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                    <TemplateConfigEditor
                        task={editingTask}
                        subtask={editingSubtask}
                        workflowType={workflowType}
                        subtaskWorkflowType={subtaskWorkflowType}
                        parentWorkflowType={parentWorkflowType}
                        uploadOnlySubtasks={selectedPhase?.defaultPhase?.settings?.uploadOnlySubtasks === true}
                        grantData={grantData}
                        xsrfToken={xsrf.value ?? ""}
                        onSave={handleSave}
                        onClose={handleCloseEditor}
                        onDeleteSubtask={editingSubtask && subtaskIsDeletable ? handleDeleteSubtask : undefined}
                        allPhaseTypes={allPhaseTypes}
                    />
                </div>
            ) : (
                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#888" }}>
                    <p>Select a task or subtask to edit.</p>
                </div>
            )}
        </div>
    );
}

export default function TemplateTasksMain(props: Props) {
    const [selectedStep, setSelectedStep] = useState<StepDto | null>(null);
    const [selectedPhase, setSelectedPhase] = useState<PhaseDto | null>(null);

    return (
        <TaskListProvider setSelectedStep={setSelectedStep} setSelectedPhase={setSelectedPhase} tasklistMode="templates">
            <TemplateTasksInner {...props} />
        </TaskListProvider>
    );
}
